import { useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { JobsList } from './components/JobsList';
import type { JobsFilterState, JobsScope } from './types/jobs';
import { BADGE_STATUSES } from './types/jobs';
import type { JobStatus } from './types/monitoring';
import './index.css';

function parseStatus(raw: string | null): JobStatus | null {
  // Reuse the badge-status set minus the UI-only 'unknown' sentinel.
  if (raw && raw !== 'unknown' && BADGE_STATUSES.has(raw as JobStatus)) {
    return raw as JobStatus;
  }
  return null;
}

function parseFilters(params: URLSearchParams): JobsFilterState {
  const scope: JobsScope = params.get('scope') === 'all' ? 'all' : 'mine';
  const search = params.get('search') ?? '';
  const rawPage = parseInt(params.get('page') ?? '1', 10);
  const page = Number.isFinite(rawPage) && rawPage >= 1 ? rawPage : 1;
  const status = parseStatus(params.get('status'));
  const waiting = params.get('waiting') === '1';
  return { scope, search, page, status, waiting };
}

function serializeFilters(
  prev: URLSearchParams,
  next: JobsFilterState
): URLSearchParams {
  const out = new URLSearchParams(prev);
  if (next.scope === 'mine') {
    out.delete('scope');
  } else {
    out.set('scope', next.scope);
  }
  if (next.search === '') {
    out.delete('search');
  } else {
    out.set('search', next.search);
  }
  if (next.status === null) {
    out.delete('status');
  } else {
    out.set('status', next.status);
  }
  if (next.waiting) {
    out.set('waiting', '1');
  } else {
    out.delete('waiting');
  }
  if (next.page === 1) {
    out.delete('page');
  } else {
    out.set('page', String(next.page));
  }
  return out;
}

const JobsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = parseFilters(searchParams);

  const setFilters = useCallback(
    (partial: Partial<JobsFilterState>) => {
      setSearchParams(prev => {
        const current = parseFilters(prev);
        const merged: JobsFilterState = { ...current, ...partial };
        // Changing any filter other than `page` itself resets paging to 1.
        if (
          ('scope' in partial && partial.scope !== current.scope) ||
          ('search' in partial && partial.search !== current.search) ||
          ('status' in partial && partial.status !== current.status) ||
          ('waiting' in partial && partial.waiting !== current.waiting)
        ) {
          merged.page = 1;
        }
        return serializeFilters(prev, merged);
      });
    },
    [setSearchParams]
  );

  return <JobsList filters={filters} setFilters={setFilters} />;
};

export default JobsPage;
